import React, { CSSProperties } from 'react';
import { Table, Header, flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import { SkeletonLoading } from '@saleshandy/design-system';
import { ChevronUp, ChevronDown } from '@saleshandy/icons';

import { SortOrder } from '../../../../enums/order';
import Checkbox from '../../../components/atoms/checkbox';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';

type IProps = {
  table: Table<any>;
  headerIndex: number;
  header: Header<any, any>;
  sort: {
    sortBy: string;
    order: SortOrder;
  } | null;
  onSort?: ((sortBy: string, order: SortOrder) => void) | null;
  checkboxState: {
    isChecked: boolean;
    isIntermediate: boolean;
    isDisabled?: boolean;
  };
  onRowSelectAllHandler: (isSelect: boolean) => void;
  isLoading?: boolean;
  enableRowSelection?: boolean;
};

const ColumnHeader: React.FC<IProps> = ({
  table,
  headerIndex,
  header,
  sort,
  onSort,
  checkboxState,
  onRowSelectAllHandler,
  isLoading = false,
  enableRowSelection = true,
}) => {
  const { column } = header;
  const columnId = header.id;
  const isSelectionColumn = headerIndex === 0 && enableRowSelection;

  const getSortState = () => {
    if (!sort) return null;

    const columnMeta = column.columnDef.meta as any;
    const sortBy = columnMeta?.sortBy;

    if (sortBy === sort.sortBy) {
      return sort.order;
    }
    return null;
  };

  const handleSort = () => {
    if (!onSort) return;

    const columnMeta = column.columnDef.meta as any;
    const sortBy = columnMeta?.sortBy;

    if (!sortBy) return;

    const currentSortState = getSortState();
    let newOrder: SortOrder;

    if (currentSortState === null) {
      newOrder = SortOrder.ASC;
    } else if (currentSortState === SortOrder.ASC) {
      newOrder = SortOrder.DESC;
    } else {
      newOrder = SortOrder.ASC;
    }

    onSort(columnId, newOrder);
  };

  const getColumnHeaderStyles = (): CSSProperties => {
    const isPinned = header.column.getIsPinned();
    const columnDefSize = (header.column.columnDef.size as number) || 0;
    const currentSize = header.getSize();
    const finalSize = currentSize > 0 ? currentSize : columnDefSize || 50;

    return {
      width: `calc(var(--header-${columnId}-size, ${finalSize}) * 1px)`,
      minWidth: `${finalSize}px`,
      left:
        isPinned === 'left' ? `${header.column.getStart('left')}px` : undefined,
      right:
        isPinned === 'right'
          ? `${header.column.getAfter('right')}px`
          : undefined,
    };
  };

  const getColumnHeaderClassNames = () => {
    const isPinned = header.column.getIsPinned();
    const isLastPinnedLeft =
      isPinned === 'left' && header.column.getIsLastColumn('left');
    const isLastPinnedRight =
      isPinned === 'right' && header.column.getIsLastColumn('right');
    const columnMeta = header.column.columnDef.meta as
      | Record<string, unknown>
      | undefined;
    const headerClassName = columnMeta?.headerClassName as string | undefined;

    return classNames(
      'tanstack-table--th',
      {
        'th--pinned': isPinned,
        'th--pinned__left': isLastPinnedLeft,
        'th--pinned__right': isLastPinnedRight,
      },
      headerClassName,
    );
  };

  const getColumnHeaderResizerClassNames = () => {
    const isResizing = header.column.getIsResizing();

    return classNames(
      'column-header-resizer',
      table.options.columnResizeDirection,
      {
        'column-header-resizer-active': isResizing,
        isResizing,
      },
    );
  };

  const sortState = getSortState();
  const columnMeta = column.columnDef.meta as any;
  const isSortable = !!columnMeta?.sortBy && !!onSort;
  const useCustomSortHeader = columnMeta?.customSortHeader === true;

  const getSortingIconsWrapperClassNames = () =>
    classNames('column-header-content--sorting-icons', {
      active: sortState !== null,
    });

  const getSortingIconClassNames = (order: SortOrder) =>
    classNames('sorting-icon', order, {
      sorted: sortState === order,
    });

  // Get header skeleton from column definition meta or columnDef
  const getHeaderSkeleton = () => {
    const skeleton =
      columnMeta?.headerSkeleton || (column.columnDef as any).headerSkeleton;

    if (skeleton) {
      return skeleton;
    }

    // Default skeleton if not provided
    return <SkeletonLoading width={100} height={20} />;
  };

  return (
    <div
      className={getColumnHeaderClassNames()}
      style={getColumnHeaderStyles()}
    >
      <div className="column-header-content">
        <div className="column-header-content--name">
          {isSelectionColumn ? (
            <>
              {enableRowSelection && !isLoading && (
                <div className="column-header-content--checkbox">
                  <Checkbox
                    checked={checkboxState.isChecked}
                    intermediate={checkboxState.isIntermediate}
                    onChange={() =>
                      onRowSelectAllHandler(!checkboxState.isChecked)
                    }
                    disabled={checkboxState.isDisabled || isLoading}
                    className={
                      checkboxState.isDisabled ? 'show-disable-style' : ''
                    }
                  />
                </div>
              )}
              <span className="column-header-content--headText">
                <span className="column-header-content--colHeadText">
                  {isLoading
                    ? getHeaderSkeleton()
                    : flexRender(column.columnDef.header, header.getContext())}
                </span>
              </span>
            </>
          ) : (
            <div
              className="column-header-content--headText"
              {...(isSortable && !isLoading && !useCustomSortHeader
                ? accessibleOnClick(handleSort)
                : {})}
              style={{
                cursor:
                  isSortable && !isLoading && !useCustomSortHeader
                    ? 'pointer'
                    : 'default',
              }}
            >
              <span className="column-header-content--colHeadText">
                {isLoading
                  ? getHeaderSkeleton()
                  : flexRender(column.columnDef.header, header.getContext())}
              </span>

              {isSortable && !isLoading && !useCustomSortHeader && (
                <div className={getSortingIconsWrapperClassNames()}>
                  <div
                    className={getSortingIconClassNames(SortOrder.ASC)}
                    {...accessibleOnClick((event: React.MouseEvent) => {
                      event.stopPropagation();
                      onSort!(columnId, SortOrder.ASC);
                    })}
                  >
                    <ChevronUp />
                  </div>
                  <div
                    className={getSortingIconClassNames(SortOrder.DESC)}
                    {...accessibleOnClick((event: React.MouseEvent) => {
                      event.stopPropagation();
                      onSort!(columnId, SortOrder.DESC);
                    })}
                  >
                    <ChevronDown />
                  </div>
                </div>
              )}
            </div>
            // ) : (
            //   <>
            //     <div className="column-header-content--headText">
            //       <span className="column-header-content--colHeadText">
            //         {isLoading
            //           ? getHeaderSkeleton()
            //           : flexRender(column.columnDef.header, header.getContext())}
            //       </span>
            //     </div>
            //     {isSortable && !isLoading && (
            //       <div className={getSortingIconsWrapperClassNames()}>
            //         <div
            //           className={getSortingIconClassNames(SortOrder.ASC)}
            //           {...accessibleOnClick((event) => {
            //             event.stopPropagation();
            //             onSort!(columnId, SortOrder.ASC);
            //           })}
            //         >
            //           <ChevronUp />
            //         </div>
            //         <div
            //           className={getSortingIconClassNames(SortOrder.DESC)}
            //           {...accessibleOnClick((event) => {
            //             event.stopPropagation();
            //             onSort!(columnId, SortOrder.DESC);
            //           })}
            //         >
            //           <ChevronDown />
            //         </div>
            //       </div>
            //     )}
            //   </>
          )}
        </div>
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={getColumnHeaderResizerClassNames()}
        onDoubleClick={() => header.column.resetSize()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
        role="separator"
        aria-label={`Resize ${column.columnDef.header}`}
      >
        <div className="column-header-resizer--handle" />
      </div>
    </div>
  );
};

export default ColumnHeader;
